import React, { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { type BottomInputAreaProps } from './BottomInputArea';
import { type TopHeaderProps } from './TopHeader';
import { HomePage } from '../pages/HomePage';
import { AiChatPage, type AiChatPageRef } from '../pages/AiChatPage';
import { DifyCapabilityPage } from '../pages/DifyCapabilityPage';
import { AiHistoryPage } from '../pages/AiHistoryPage';
import { ThemeProvider } from '../contexts/ThemeContext';

export interface MainContentProps extends TopHeaderProps, BottomInputAreaProps {
  readonly defaultCapabilityCode?: string;
  readonly activeTab: 'home' | 'create' | 'search' | 'discover' | 'dify' | 'history';
  readonly businessType?: string;
  readonly businessId?: string | number;
  readonly onCreateConversation?: () => string | void;
  readonly onTabChange?: (tab: 'home' | 'create' | 'search' | 'discover' | 'dify' | 'history') => void;
}

/** 暴露给父组件的方法 */
export interface MainContentRef {
  /** 创建新会话 */
  createNewConversation: () => string;
}

export const MainContent = forwardRef<MainContentRef, MainContentProps>(({
  modelLabel,
  activeTab,
  defaultCapabilityCode,
  onClickUpgrade,

  onChangeInput,
  onClickMic,
  onClickSend,

  onClickAction1,
  onClickAction2,
  onClickAction3,
  onClickAction4,
  onClickDots,

  onCreateConversation,
  onTabChange,
  businessType = 'default',
  businessId = 'default'
},ref) => {
  const aiChatPageRef = useRef<AiChatPageRef>(null);

  // 用于暂存从历史页跳转过来的会话ID
  const [jumpConversationId, setJumpConversationId] = useState<number | undefined>(undefined);

  // 暴露创建新会话的方法
  useImperativeHandle(ref, () => ({
    createNewConversation: (): string => {
      if (aiChatPageRef.current) {
        return aiChatPageRef.current.resetConversation();
      }
      return `conv_${Date.now()}`;
    }
  }), []);

  const handleJumpToChat = useCallback((id: number) => {
    // 先清空，再设置，确保同一会话重复跳转时也能触发 useEffect
    setJumpConversationId(undefined);
    // 使用 setTimeout 确保 React 先处理 undefined，再处理新值
    setTimeout(() => {
      setJumpConversationId(id);
      onTabChange?.('create');
      console.log('[MainContent] jumpConversationId set to:', id);
    }, 0);
  }, [onTabChange]);

  return (
    <main className="main-content">
      {activeTab === 'home' ? <HomePage /> : null}

      {activeTab === 'create' ? (
        <ThemeProvider defaultTheme="light">
          <AiChatPage
            ref={aiChatPageRef}
            modelLabel={modelLabel}
            defaultCapabilityCode={defaultCapabilityCode}
            onClickUpgrade={onClickUpgrade}
            onChangeInput={onChangeInput}
            onClickMic={onClickMic}
            onClickSend={onClickSend}
            onClickAction1={onClickAction1}
            onClickAction2={onClickAction2}
            onClickAction3={onClickAction3}
            onClickAction4={onClickAction4}
            onClickDots={onClickDots}
            businessType={businessType}
            businessId={businessId}
            defaultConversationId={jumpConversationId}
          />
        </ThemeProvider>
      ) : null}

      {activeTab === 'dify' ? <DifyCapabilityPage /> : null}
      {activeTab === 'history' ? (
        <AiHistoryPage
          onJumpToChat={handleJumpToChat}
        />
      ) : null}

      <span
        style={{ display: 'none' }}
        data-ai-chatbot-create-conv-hook
        onClick={onCreateConversation as any}
      />
    </main>
  );
});